import { useState } from "react";
import * as XLSX from "xlsx";
import { format, isValid, parse } from "date-fns";
import { supabase } from '@/integrations/supabase/client';
import { useToast } from "@/components/ui/use-toast";
import { toLocalISOString } from '@/utils/timezoneUtils';

export const useScheduleUploader = (teamId: string) => {
  const { toast } = useToast();
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState<any[]>([]);
  const [errors, setErrors] = useState<string[]>([]);

  const downloadTemplate = () => {
    console.log("Generating Excel template...");
    const workbook = XLSX.utils.book_new();
    
    // Create template with example data
    const templateData = [
      {
        "Shift Title": "Main Gate",
        "Description": "Guard the main entrance",
        "Date (DD/MM/YYYY)": format(new Date(), "dd/MM/yyyy"),
        "Start Time (HH:MM)": "09:00",
        "End Time (HH:MM)": "17:00",
        "Guard First Name": "First Name",
        "Guard Last Name": "Last Name",
        "Color": "primary"
      },
      {
        "Shift Title": "ΡΕΠΟ",
        "Description": "Day off",
        "Date (DD/MM/YYYY)": format(new Date(), "dd/MM/yyyy"),
        "Start Time (HH:MM)": "00:00",
        "End Time (HH:MM)": "00:00",
        "Guard First Name": "First Name",
        "Guard Last Name": "Last Name",
        "Color": "secondary"
      }
    ];

    const sheet = XLSX.utils.json_to_sheet(templateData);
    XLSX.utils.book_append_sheet(workbook, sheet, "Template");
    
    // Add instructions sheet
    const instructionsData = [
      ["INSTRUCTIONS FOR BULK UPLOADING SHIFTS"],
      [""],
      ["1. Follow this template exactly - DO NOT change column names"],
      ["2. Date must be in DD/MM/YYYY format (e.g. 01/02/2025) or D/M/YY (e.g. 10/2/25)"],
      ["3. Time must be in HH:MM format (e.g. 09:00, 17:30) or in any of the following formats:"],
      ["   - HH:MM (e.g., 08:00)"],
      ["   - H:MM (e.g., 8:00)"],
      ["   - HH.MM (e.g., 08.00)"],
      ["   - HHMM (e.g., 0800)"],
      ["   - 12-hour format (e.g., 8AM, 2PM)"],
      ["4. First Name and Last Name must be in separate columns"],
      ["5. Guard's First and Last Name must match exactly how they appear in the system"],
      ["6. Color can be: primary, secondary, or destructive"],
      ["7. Guards cannot have overlapping schedules"],
      ["8. For ΡΕΠΟ (day off), you can use 00:00 to 00:00 to indicate the whole day"]
    ];
    const instructionsSheet = XLSX.utils.aoa_to_sheet(instructionsData);
    XLSX.utils.book_append_sheet(workbook, instructionsSheet, "Instructions");
    
    // Generate file and trigger download
    XLSX.writeFile(workbook, "shift-template.xlsx");
    console.log("Template downloaded");
  };

  const parseTimeString = (timeStr: string | number): { hour: number, minute: number } | null => {
    if (timeStr === undefined || timeStr === null) return null;
    
    if (typeof timeStr === 'number') {
      console.log(`Parsing numeric time value: ${timeStr}`);
      const totalMinutes = Math.round(timeStr * 24 * 60);
      const hour = Math.floor(totalMinutes / 60);
      const minute = totalMinutes % 60;
      return { hour, minute };
    }
    
    const timeString = String(timeStr).trim();
    console.log(`Parsing time string: "${timeString}"`);
    
    let match = timeString.match(/^(\d{1,2}):(\d{2})$/);
    if (match) {
      return {
        hour: parseInt(match[1], 10),
        minute: parseInt(match[2], 10)
      };
    }
    
    match = timeString.match(/^(\d{1,2})\.(\d{2})$/);
    if (match) {
      return {
        hour: parseInt(match[1], 10),
        minute: parseInt(match[2], 10)
      };
    }
    
    match = timeString.match(/^(\d{2})(\d{2})$/);
    if (match) {
      return {
        hour: parseInt(match[1], 10),
        minute: parseInt(match[2], 10)
      };
    }
    
    match = timeString.match(/^(\d{1})(\d{2})$/);
    if (match) {
      return {
        hour: parseInt(match[1], 10),
        minute: parseInt(match[2], 10)
      };
    }
    
    match = timeString.match(/^(\d{1,2})(?::|\.)??(\d{2})??\s*(AM|PM|am|pm|a|p)$/);
    if (match) {
      let hour = parseInt(match[1], 10);
      const minute = match[2] ? parseInt(match[2], 10) : 0;
      const period = match[3].toLowerCase();
      
      if (period === 'pm' || period === 'p') {
        if (hour < 12) hour += 12;
      } else if (period === 'am' || period === 'a') {
        if (hour === 12) hour = 0;
      }
      
      return { hour, minute };
    }
    
    if (!isNaN(Number(timeString))) {
      const excelTime = parseFloat(timeString);
      if (excelTime >= 0 && excelTime < 1) {
        const totalMinutes = Math.round(excelTime * 24 * 60);
        const hour = Math.floor(totalMinutes / 60);
        const minute = totalMinutes % 60;
        return { hour, minute };
      }
    }
    
    console.error(`Failed to parse time string: "${timeString}"`);
    return null;
  };
  
  const parseDateString = (dateStr: string): Date | null => {
    if (!dateStr) return null;
    
    dateStr = dateStr.toString().trim();
    console.log(`Parsing date string: "${dateStr}"`);
    
    const formats = [
      'dd/MM/yyyy',
      'MM/dd/yyyy',
      'd/M/yyyy',
      'M/d/yyyy',
      'dd-MM-yyyy',
      'MM-dd-yyyy',
      'dd.MM.yyyy',
      'MM.dd.yyyy',
      'yyyy-MM-dd',
      'yyyy/MM/dd',
      'd/M/yy',
      'dd/MM/yy',
      'MM/dd/yy'
    ];
    
    for (const formatStr of formats) {
      try {
        const parsedDate = parse(dateStr, formatStr, new Date());
        if (isValid(parsedDate)) {
          console.log(`Successfully parsed date "${dateStr}" with format "${formatStr}": ${parsedDate}`);
          return parsedDate;
        }
      } catch (error) {
      }
    }
    
    if (!isNaN(Number(dateStr))) {
      const excelSerialDate = parseInt(dateStr, 10);
      if (excelSerialDate > 18264 && excelSerialDate < 47482) {
        const date = new Date(Date.UTC(0, 0, excelSerialDate - 1));
        if (isValid(date)) {
          console.log(`Parsed Excel serial date ${excelSerialDate}: ${date}`);
          return date;
        }
      }
    }
    
    console.error(`Failed to parse date string: "${dateStr}"`);
    return null;
  };
  
  const findGuard = async (firstName: string, lastName: string): Promise<string | null> => {
    try {
      console.log(`Finding guard: ${firstName} ${lastName}`);
      
      if (!firstName.trim() || !lastName.trim()) {
        console.log("First name or last name is empty");
        return null;
      }
      
      const { data, error } = await supabase
        .from('profiles')
        .select('id, first_name, last_name')
        .eq('status', 'active');
      
      if (error) {
        console.error("Error fetching guards:", error);
        throw error;
      }
      
      const exactMatch = data.find(g => 
        g.first_name?.toLowerCase() === firstName.toLowerCase() && 
        g.last_name?.toLowerCase() === lastName.toLowerCase()
      );
      
      if (exactMatch) {
        console.log(`Found exact match guard: ${exactMatch.id}`);
        return exactMatch.id;
      }
      
      console.log(`No guard found for: ${firstName} ${lastName}`);
      return null;
      
    } catch (error) {
      console.error("Error in findGuard:", error);
      return null;
    }
  };
  
  const createShifts = async (shiftsData: any[]): Promise<any[]> => {
    try {
      if (!shiftsData.length) return [];
      
      console.log(`Batch creating ${shiftsData.length} shifts`);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error("No authenticated user found");
      }
      
      const shiftsWithCreator = shiftsData.map(shift => ({
        ...shift,
        created_by: user.id
      }));
      
      const { data, error } = await supabase
        .from('team_schedules')
        .insert(shiftsWithCreator)
        .select();
      
      if (error) {
        console.error("Error creating shifts:", error);
        throw error;
      }
      
      console.log(`Successfully created ${data.length} shifts`);
      
      // Logging handled automatically by database trigger
      console.log('Bulk upload completed, logging handled by database trigger');
      
      return data || [];
    } catch (error: any) {
      console.error("Error in batch creating shifts:", error);
      throw error;
    }
  };
  
  const createShift = async (shiftData: any): Promise<any> => {
    try {
      console.log(`Creating shift:`, shiftData);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error("No authenticated user found");
      }
      
      const shiftWithCreator = {
        ...shiftData,
        created_by: user.id
      };
      
      const { data, error } = await supabase
        .from('team_schedules')
        .insert(shiftWithCreator)
        .select()
        .single();
      
      if (error) {
        console.error("Error creating shift:", error);
        throw error;
      }
      
      console.log("Shift created successfully:", data);
      return data;
    } catch (error: any) {
      console.error("Error in createShift:", error);
      throw error;
    }
  };
  
  const uploadFile = async (file: File) => {
    console.log("Starting upload process with file:", file.name);
    
    setIsUploading(true);
    setProgress(5);
    setResults([]);
    setErrors([]);
    
    try {
      const reader = new FileReader();
      
      const readerPromise = new Promise<ArrayBuffer>((resolve, reject) => {
        reader.onload = (e) => {
          if (e.target?.result) {
            resolve(e.target.result as ArrayBuffer);
          } else {
            reject(new Error("Failed to read file"));
          }
        };
        reader.onerror = () => reject(reader.error);
      });
      
      reader.readAsArrayBuffer(file);
      const data = await readerPromise;
      
      setProgress(20);
      
      const workbook = XLSX.read(data, { type: 'array' });
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];
      
      const rows = XLSX.utils.sheet_to_json(worksheet);
      console.log(`Loaded ${rows.length} rows from Excel`);
      
      if (rows.length === 0) {
        throw new Error("No data found in the file");
      }
      
      setProgress(30);
      
      const requiredColumns = ["Shift Title", "Date (DD/MM/YYYY)", "Start Time (HH:MM)", "End Time (HH:MM)"];
      const firstRow = rows[0] as any;
      
      const missingColumns = requiredColumns.filter(col => !(col in firstRow));
      if (missingColumns.length > 0) {
        throw new Error(`Missing required columns: ${missingColumns.join(", ")}`);
      }
      
      setProgress(40);
      
      const successfulShifts = [];
      const errorMessages = [];
      const shiftsToCreate = [];
      const batchSize = 50; // Process 50 shifts at a time
      
      console.log(`Starting to process ${rows.length} rows with batch size ${batchSize}`);
      
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i] as any;
        const currentProgress = 40 + Math.round((i / rows.length) * 50);
        setProgress(currentProgress);
        
        try {
          if (!row["Shift Title"] || !row["Date (DD/MM/YYYY)"]) {
            throw new Error(`Row ${i + 1}: Missing required fields (title or date)`);
          }
          
          const parsedDate = parseDateString(row["Date (DD/MM/YYYY)"]);
          if (!parsedDate) {
            throw new Error(`Row ${i + 1}: Invalid date format: ${row["Date (DD/MM/YYYY)"]}. Use DD/MM/YYYY format.`);
          }
          
          const isRepoEntry = (
            row["Shift Title"]?.toUpperCase()?.includes("ΡΕΠΟ") || 
            row["Shift Title"]?.toUpperCase()?.includes("REPO")
          );
          
          let startTime, endTime;
          
          if (isRepoEntry) {
            startTime = parseTimeString(row["Start Time (HH:MM)"]) || { hour: 0, minute: 0 };
            endTime = parseTimeString(row["End Time (HH:MM)"]) || { hour: 0, minute: 0 };
          } else {
            startTime = parseTimeString(row["Start Time (HH:MM)"]);
            if (!startTime) {
              throw new Error(`Row ${i + 1}: Start and end times are required`);
            }
            
            endTime = parseTimeString(row["End Time (HH:MM)"]);
            if (!endTime) {
              throw new Error(`Row ${i + 1}: Start and end times are required`);
            }
          }
          
          const startDate = new Date(parsedDate);
          startDate.setHours(startTime.hour, startTime.minute, 0, 0);
          
          const endDate = new Date(parsedDate);
          endDate.setHours(endTime.hour, endTime.minute, 0, 0);
          
          if (!isRepoEntry && (endTime.hour < startTime.hour || 
              (endTime.hour === startTime.hour && endTime.minute < startTime.minute))) {
            console.log(`Row ${i + 1}: End time appears to be on the next day, adjusting date`);
            endDate.setDate(endDate.getDate() + 1);
          }
          
          const shiftData = {
            team_id: teamId,
            title: row["Shift Title"],
            description: row["Description"] || "",
            start_date: toLocalISOString(startDate),
            end_date: toLocalISOString(endDate),
            color: row["Color"] || "primary",
            assigned_guards: [] as string[]
          };
          
          if (row["Guard First Name"] && row["Guard Last Name"]) {
            const guardId = await findGuard(
              row["Guard First Name"].toString(), 
              row["Guard Last Name"].toString()
            );
            
            if (guardId) {
              shiftData.assigned_guards = [guardId];
            } else {
              console.warn(`Row ${i + 1}: Guard not found: ${row["Guard First Name"]} ${row["Guard Last Name"]}`);
              errorMessages.push(`Row ${i + 1}: Guard not found: ${row["Guard First Name"]} ${row["Guard Last Name"]}`);
            }
          }
          
          shiftsToCreate.push(shiftData);
          console.log(`Added shift ${i + 1} to batch. Current batch size: ${shiftsToCreate.length}. Shift date: ${format(startDate, 'yyyy-MM-dd')}`);
          
          // Process batch when it reaches the limit OR when we're at the last row
          const shouldProcessBatch = shiftsToCreate.length >= batchSize || i === rows.length - 1;
          
          if (shouldProcessBatch && shiftsToCreate.length > 0) {
            console.log(`Processing batch of ${shiftsToCreate.length} shifts at row ${i + 1}. Remaining rows: ${rows.length - i - 1}`);
            
            try {
              const createdShifts = await createShifts(shiftsToCreate);
              successfulShifts.push(...createdShifts);
              console.log(`Batch processed successfully: ${createdShifts.length} shifts created. Total so far: ${successfulShifts.length}`);
            } catch (batchError: any) {
              console.error("Error creating batch of shifts:", batchError);
              
              // Fallback: try creating shifts individually
              for (const shift of shiftsToCreate) {
                try {
                  const createdShift = await createShift(shift);
                  successfulShifts.push(createdShift);
                } catch (singleError: any) {
                  errorMessages.push(`Error creating shift: ${singleError.message}`);
                }
              }
            }
            
            // Clear the batch for next iteration
            shiftsToCreate.length = 0;
            console.log(`Batch cleared. Total shifts created so far: ${successfulShifts.length}. Continuing with remaining ${rows.length - i - 1} rows...`);
          }
          
        } catch (error: any) {
          console.error(`Error processing row ${i + 1}:`, error);
          errorMessages.push(error.message || `Error in row ${i + 1}`);
        }
      }
      
      // Final check - process any remaining shifts in the batch
      if (shiftsToCreate.length > 0) {
        console.log(`Processing final batch of ${shiftsToCreate.length} remaining shifts`);
        try {
          const createdShifts = await createShifts(shiftsToCreate);
          successfulShifts.push(...createdShifts);
          console.log(`Final batch processed: ${createdShifts.length} shifts created. Final total: ${successfulShifts.length}`);
        } catch (batchError: any) {
          console.error("Error creating final batch of shifts:", batchError);
          
          // Fallback: try creating shifts individually
          for (const shift of shiftsToCreate) {
            try {
              const createdShift = await createShift(shift);
              successfulShifts.push(createdShift);
            } catch (singleError: any) {
              errorMessages.push(`Error creating shift: ${singleError.message}`);
            }
          }
        }
      }
      
      setProgress(95);
      
      console.log(`Upload completed. Total processed: ${rows.length}, Successful: ${successfulShifts.length}, Errors: ${errorMessages.length}`);
      setResults(successfulShifts);
      setErrors(errorMessages);
      
      setProgress(100);
      return successfulShifts;
      
    } catch (error: any) {
      console.error("Error uploading file:", error);
      setErrors([error.message || "Unknown error during upload"]);
      throw error;
    } finally {
      setIsUploading(false);
    }
  };
  
  return {
    uploadFile,
    downloadTemplate,
    isUploading,
    progress,
    results,
    errors
  };
};
